import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import Masonry from 'react-masonry-css';
import ArtworkModal from '@/components/ArtworkModal';
import ShareButtons from '@/components/ShareButtons';
import WishlistButton from '@/components/WishlistButton';
import ProgressiveImage from '@/components/ProgressiveImage';
import { Search, ShoppingCart, Printer, Download, Loader2, ArrowLeft } from 'lucide-react';
import { useCart } from '@/context/CartContext';
import { useCurrency } from '@/context/RegionContext';
import { toast } from 'sonner';
import SEO from '@/components/SEO';
import { trackArtworkView } from '@/components/PageTracker';
import { Button } from '@/components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 12;

const PORTRAIT_TYPES = {
  person: { label: 'Person Portraits', description: 'Individual and character portrait paintings by Gangadhar Som.' },
  family: { label: 'Family Portraits', description: 'Family portrait paintings capturing bonds and togetherness.' },
  pet: { label: 'Pet Portraits', description: 'Pet portrait paintings bringing your beloved companions to life on canvas.' },
};

// Category names that belong on each page. The admin dropdown uses "Portrait - Person/Family/Pet", but
// artworks were also saved with the AI's free-text names ("Portrait", "Pets", "Family Portrait", ...),
// so all of those are accepted. (Generic "Portrait" and "Kids Portrait" are listed under Person.)
const PORTRAIT_CATEGORIES = {
  person: ['Portrait - Person', 'Portrait', 'Person Portrait', 'Kids Portrait'],
  family: ['Portrait - Family', 'Family Portrait'],
  pet: ['Portrait - Pet', 'Pets', 'Pet', 'Pet Portrait'],
};
const ALL_PORTRAIT_CATEGORIES = Object.values(PORTRAIT_CATEGORIES).flat();

const Portraits = () => {
  const { type } = useParams();
  const [artworks, setArtworks] = useState([]);
  const [selectedArtwork, setSelectedArtwork] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const { addToCart } = useCart();
  const { formatPrice } = useCurrency();
  const navigate = useNavigate();

  const portraitInfo = type ? PORTRAIT_TYPES[type] : null;

  const pageTitle = portraitInfo
    ? portraitInfo.label
    : 'Portraits';
  const pageDescription = portraitInfo
    ? portraitInfo.description
    : 'Browse portrait paintings by Gangadhar Som. Person, family, and pet portraits.';

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const fetchArtworks = useCallback(async (pageNum, append = false) => {
    try {
      if (append) setLoadingMore(true); else setLoading(true);
      const params = new URLSearchParams({ page: pageNum, limit: PAGE_SIZE });
      params.set('categories', (PORTRAIT_CATEGORIES[type] || ALL_PORTRAIT_CATEGORIES).join(','));
      if (debouncedSearch) params.set('search', debouncedSearch);

      const res = await axios.get(`${API}/artworks?${params}`);
      const data = res.data;
      if (data.items) {
        setArtworks(prev => append ? [...prev, ...data.items] : data.items);
        setTotalPages(data.pages);
        setTotal(data.total);
      } else {
        const items = Array.isArray(data) ? data : [];
        setArtworks(prev => append ? [...prev, ...items] : items);
        setTotalPages(1);
        setTotal(items.length);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [type, debouncedSearch]);

  useEffect(() => {
    setPage(1);
    fetchArtworks(1, false);
  }, [fetchArtworks]);

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchArtworks(nextPage, true);
  };

  const handleAddToCart = (e, artwork) => {
    e.stopPropagation();
    addToCart({
      id: artwork.id, title: artwork.title,
      price: artwork.price || (artwork.art_type === 'Digital' ? 5000 : 10000),
      image_url: artwork.image_url, art_type: artwork.art_type, category: artwork.category,
    });
    toast.success(`"${artwork.title}" added to cart`);
  };

  const breakpointColumns = { default: 3, 1024: 2, 640: 1 };

  const tabs = [
    { key: null, label: 'All Portraits', path: '/portraits' },
    { key: 'person', label: 'Person', path: '/portraits/person' },
    { key: 'family', label: 'Family', path: '/portraits/family' },
    { key: 'pet', label: 'Pet', path: '/portraits/pet' },
  ];

  return (
    <div className="min-h-screen pt-24 sm:pt-32 pb-16 sm:pb-24" data-testid="portraits-page">
      <SEO
        title={`${pageTitle} - Gangadhar Som | Commission Portrait Art`}
        description={pageDescription}
        keywords={`Gangadhar Som, ${pageTitle.toLowerCase()}, portrait painting, commission portrait, ${type || 'person family pet'} portrait art`}
        url={`${window.location.origin}${type ? `/portraits/${type}` : '/portraits'}`}
      />
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-12 lg:px-24">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-10 sm:mb-14">
          <Link to="/gallery" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4" data-testid="back-to-gallery">
            <ArrowLeft size={16} /> Gallery
          </Link>
          <span className="font-sans uppercase tracking-[0.2em] text-xs font-medium text-muted-foreground block">Portraits</span>
          <h1 className="font-serif text-3xl sm:text-4xl md:text-6xl font-light tracking-tight mt-2" data-testid="portraits-page-title">
            {pageTitle}
          </h1>
          <p className="font-sans text-sm text-muted-foreground mt-2 max-w-xl">{pageDescription}</p>
        </motion.div>

        {/* Type Tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          {tabs.map(tab => (
            <Link
              key={tab.path}
              to={tab.path}
              data-testid={`portrait-tab-${tab.key || 'all'}`}
              className={`px-5 py-2 rounded-full font-sans uppercase tracking-[0.12em] text-[10px] sm:text-xs font-medium transition-all ${
                (type || null) === tab.key
                  ? 'bg-accent text-white shadow-md'
                  : 'bg-card border border-border hover:border-accent'
              }`}
            >
              {tab.label}
            </Link>
          ))}
        </div>

        {/* Search */}
        <div className="relative max-w-md mb-8">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
          <input
            type="text" placeholder="Search portraits..." value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)} data-testid="portrait-search-input"
            className="w-full pl-11 pr-4 py-2.5 bg-card border border-border rounded-full font-sans text-sm focus:outline-none focus:ring-2 focus:ring-accent transition-shadow"
          />
        </div>

        {/* Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="portraits-loading">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="skeleton aspect-[4/5] rounded-lg" />
            ))}
          </div>
        ) : artworks.length === 0 ? (
          <div className="text-center py-20" data-testid="no-portraits-message">
            <p className="font-sans text-muted-foreground mb-2">No {type || ''} portraits found yet.</p>
            <p className="font-sans text-sm text-muted-foreground">Upload artworks with category &quot;Portrait - {type ? type.charAt(0).toUpperCase() + type.slice(1) : 'Person/Family/Pet'}&quot; from the admin panel.</p>
          </div>
        ) : (
          <>
            <Masonry breakpointCols={breakpointColumns} className="masonry-grid" columnClassName="masonry-grid_column">
              {artworks.map((artwork, index) => (
                <motion.div
                  key={artwork.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(index * 0.04, 0.5) }}
                  onClick={() => { setSelectedArtwork(artwork); setIsModalOpen(true); trackArtworkView(artwork.id, artwork.title); }}
                  className="group cursor-pointer image-hover"
                  data-testid={`portrait-card-${index}`}
                >
                  <div className="artwork-frame relative overflow-hidden protected-image select-none">
                    <ProgressiveImage
                      src={artwork.thumb_url || artwork.image_url}
                      placeholder={artwork.placeholder}
                      alt={artwork.title}
                      className="w-full h-auto pointer-events-none select-none"
                    />
                    <div className="absolute inset-0 z-[1]" />
                    <div className="image-overlay z-[3]">
                      <h3 className="font-serif text-base sm:text-xl font-light text-white mb-0.5">{artwork.title}</h3>
                      <div className="flex items-center justify-between w-full">
                        <div>
                          {artwork.year && <p className="font-sans text-xs text-white/80">{artwork.year}</p>}
                          {artwork.for_sale === false ? (
                            <span className="inline-block mt-1 px-2 py-0.5 bg-red-500/90 text-white font-sans uppercase tracking-wider text-[10px] rounded-full">Sold</span>
                          ) : (
                            <p className="font-serif text-sm sm:text-lg text-white mt-0.5">
                              {formatPrice(artwork.price || 10000)}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <WishlistButton artworkId={artwork.id} variant="compact" />
                          <ShareButtons artwork={artwork} variant="compact" />
                          <button onClick={(e) => { e.stopPropagation(); navigate(`/print/${artwork.id}`); }}
                            data-testid={`portrait-order-print-${index}`}
                            className="p-2 bg-white/20 hover:bg-white/40 text-white rounded-full transition-transform hover:scale-110" title="Order Print">
                            <Printer size={16} />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); setSelectedArtwork(artwork); setIsModalOpen(true); }}
                            data-testid={`portrait-digital-download-${index}`}
                            className="p-2 bg-violet-500/80 hover:bg-violet-500 text-white rounded-full transition-transform hover:scale-110"
                            title={`Buy Digital Download — ${formatPrice(499)}`}
                          >
                            <Download size={16} />
                          </button>
                          {artwork.for_sale !== false && (
                            <button onClick={(e) => handleAddToCart(e, artwork)}
                              data-testid={`portrait-add-to-cart-${index}`}
                              className="p-2 bg-accent hover:bg-accent/80 text-white rounded-full transition-transform hover:scale-110" title="Buy Original">
                              <ShoppingCart size={16} />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </Masonry>
            {page < totalPages && (
              <div className="text-center mt-12">
                <Button variant="outline" size="lg" onClick={handleLoadMore} disabled={loadingMore}
                  data-testid="portraits-load-more" className="px-10 py-3 rounded-full font-sans uppercase tracking-[0.15em] text-xs">
                  {loadingMore ? <><Loader2 size={16} className="mr-2 animate-spin" /> Loading...</> : `Load More (${artworks.length} of ${total})`}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
      <ArtworkModal artwork={selectedArtwork} isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
};

export default Portraits;
